/**
 * Client entry point.
 *
 * Boot order: load assets → build the decor catalog from the nature manifest →
 * hand the URL hash to `initSeed`, whose callback is the ONE place a track is
 * (re)generated. Two query modes short-circuit that: `?gallery=1` is the asset
 * regression page, `?debug=1` overlays the sampled centerline.
 */
import * as THREE from "three";
import { createScene, fitShadowsTo } from "./scene.ts";
import { type Assets, loadAssets } from "./assets.ts";
import { initSeed, newSeed } from "./seed.ts";
import { buildTrack, type BuiltTrack } from "./track.ts";
import { buildCenterline, type Centerline } from "./centerline.ts";
import { createMarble, type Marble } from "./marble.ts";
import { FanField, fanVolumesOf } from "./fanField.ts";
import { type CamMode, createCameras } from "./camera.ts";
import { generateTrack } from "../gen/generateTrack.ts";
import type { DecorCatalog } from "../gen/decor.ts";
import type { Placement, Track } from "../shared/types.ts";
import { genVersion } from "../gen/version.ts";
import { installCua } from "./cua.ts";
import { installInspect } from "./inspect.ts";
import { installPhotoKey } from "./photo.ts";
import { bootEndless } from "./endless.ts";
import { createTrail } from "./trail.ts";
import { installDevUi } from "./devui.ts";
import { maybeShowMenu } from "./menu.ts";
import { buildTwistDemo } from "./twistDemo.ts";
import { buildPieceBench } from "./pieceBench.ts";
import { installRunControls } from "./runControls.ts";

const params = new URLSearchParams(location.search);
const GALLERY = params.get("gallery") === "1";
const TWIST_DEMO = params.get("demo") === "twist";
const DEBUG = params.get("debug") === "1";
const ENDLESS = params.get("endless") !== "0";

const container = document.getElementById("app")!;
const seedEl = document.getElementById("seed");
const statsEl = document.getElementById("stats");
const newTrackBtn = document.getElementById("new-track") as
  | HTMLButtonElement
  | null;
const camBtn = document.getElementById("cam-mode") as HTMLButtonElement | null;
const modeBtn = document.getElementById("mode-toggle") as
  | HTMLButtonElement
  | null;

// Main menu (settings entry point): owns bare urls and ?menu=1; every
// shared seed link boots straight into the game.
const MENU_ACTIVE = maybeShowMenu();
const loadingEl = document.getElementById("loading")!;
if (MENU_ACTIVE) loadingEl.hidden = true;
document.getElementById("run-mode")!.textContent = ENDLESS
  ? "Endless run"
  : "Classic loop";
for (const el of document.querySelectorAll<HTMLElement>(".endless-help")) {
  el.hidden = !ENDLESS;
}

// Mode switch is a real navigation (search param changes, hash seed kept).
if (modeBtn) {
  modeBtn.textContent = ENDLESS ? "loop mode" : "endless mode";
  modeBtn.addEventListener("click", () => {
    const q = new URLSearchParams(location.search);
    q.set("endless", ENDLESS ? "0" : "1");
    location.href = `${location.pathname}?${q}${location.hash}`;
  });
}
// Back to the menu, keeping the seed and this session's settings.
const menuBtn = document.getElementById("menu-btn") as
  | HTMLButtonElement
  | null;
menuBtn?.addEventListener("click", () => {
  const q = new URLSearchParams(location.search);
  q.set("menu", "1");
  location.href = `${location.pathname}?${q.toString()}${location.hash}`;
});

installDevUi();

// The menu is its own PAGE (title screen): no game scene, no HUD — the
// render loop never starts and START navigates to a game url. The
// non-null trick is safe: every ctx consumer is behind !MENU_ACTIVE.
if (MENU_ACTIVE) {
  const hud = document.getElementById("hud");
  if (hud) hud.hidden = true;
  if (statsEl) statsEl.hidden = true;
}
const ctx = MENU_ACTIVE ? null! : createScene(container);
if (!MENU_ACTIVE) installRunControls(ctx);

function setStats(text: string): void {
  if (statsEl) statsEl.textContent = text;
}

function fail(err: unknown): void {
  console.error(err);
  setStats(`error: ${err instanceof Error ? err.message : String(err)}`);
  loadingEl.hidden = false;
  loadingEl.classList.add("failed");
  document.getElementById("loading-message")!.textContent =
    "This world couldn't load.";
  loadingEl.querySelector("span")!.textContent =
    "Return to Settings to try another world.";
}

// ---------------------------------------------------------------------------
// Normal mode
// ---------------------------------------------------------------------------

/** Catalog over every loaded decor pack (M18a). Finite mode only ever
 * draws the nature rows (pickSetting's `finite` guard), so the extra
 * packs are inert there; endless biomes reach them by name. */
function decorCatalog(assets: Assets): DecorCatalog {
  return {
    packs: [...assets.manifests].map(([slug, m]) => ({
      slug,
      ids: m.pieces.map((p) => p.id),
    })),
  };
}

function boot(assets: Assets): void {
  const catalog: DecorCatalog = decorCatalog(assets);

  let built: BuiltTrack | null = null;
  let centerline: Centerline | null = null;
  let marble: Marble | null = null;
  let debugLine: THREE.Line | null = null;
  let lastTrack: Track | null = null;
  let lastSeed = "";
  const trail = createTrail();
  const boostTrail = createTrail(0xff5a24, {
    maxPoints: 28,
    headAlpha: 1,
    additive: true,
    renderOrder: 6,
  });
  boostTrail.obj.name = "boost-trail";
  const trailPoint = new THREE.Vector3();
  ctx.scene.add(trail.obj);
  ctx.scene.add(boostTrail.obj);
  let boostTrailFade = 0;
  let boostTrailActive = false;

  const cams = createCameras(ctx, null, null);
  // P = live photo export (shared with endless mode).
  installPhotoKey(ctx, () => `marbles-${lastSeed || "loop"}`);
  const inspector = installInspect(ctx, () => ({
    seed: lastSeed,
    track: lastTrack,
    group: built?.group ?? null,
  }));
  const setCamLabel = (mode: CamMode) => {
    if (camBtn) {
      camBtn.textContent = mode === "orbit" ? "Follow cam" : "Orbit cam";
      camBtn.setAttribute("aria-pressed", String(mode === "follow"));
    }
  };
  cams.onModeChange = setCamLabel;
  setCamLabel(cams.mode);

  if (DEBUG) {
    const axes = new THREE.AxesHelper(2);
    axes.name = "axes";
    ctx.scene.add(axes);
  }

  ctx.onFrame.add((dt) => {
    if (built) {
      for (const sp of built.spinners) {
        sp.obj.rotateOnAxis(sp.axis, sp.radPerSec * dt);
      }
    }
    marble?.update(dt);
    if (marble) {
      // Ball center = the pivot child, not the group origin on the deck.
      const center = marble.obj.children[0] ?? marble.obj;
      center.getWorldPosition(trailPoint);
      trail.update(trailPoint, ctx.camera);
      if (marble.boosting) {
        if (!boostTrailActive) boostTrail.reset();
        boostTrailActive = true;
        boostTrailFade = 1;
        boostTrail.update(trailPoint, ctx.camera);
      } else {
        boostTrailActive = false;
        boostTrailFade = Math.max(0, boostTrailFade - dt * 4);
        boostTrail.fade(ctx.camera, boostTrailFade);
      }
    }
    cams.update(dt);
  });

  function regenerate(seed: string): void {
    const t0 = performance.now();

    inspector.reset();
    built?.dispose();
    built = null;
    if (marble) {
      ctx.scene.remove(marble.obj);
      marble.dispose();
      marble = null;
    }
    trail.reset();
    boostTrail.reset();
    boostTrailFade = 0;
    boostTrailActive = false;
    if (debugLine) {
      ctx.scene.remove(debugLine);
      debugLine.geometry.dispose();
      (debugLine.material as THREE.Material).dispose();
      debugLine = null;
    }
    cams.retarget(null, null);

    let track;
    try {
      track = generateTrack(seed, catalog);
    } catch (err) {
      fail(err);
      return;
    }
    // M19a: derive the loop's wind capsules once per track (family
    // defaults only — loop fans carry no wind hints, no pulses).
    const loopWindField = (placements: readonly Placement[]): FanField => {
      const field = new FanField();
      field.addSegment(0, fanVolumesOf(placements));
      return field;
    };

    lastTrack = track;
    lastSeed = seed;
    built = buildTrack(track, assets, ctx.scene);
    centerline = buildCenterline(track.loop);
    marble = createMarble(
      assets,
      centerline.curve,
      centerline.altCurve,
      track.placements
        .filter((p) => p.boost)
        .map((p) => ({
          pos: p.pos as [number, number, number],
          strength: p.boost!,
        })),
      (track.falls ?? []).map((f) => ({
        pos: f.pos as [number, number, number],
        landPos: f.landPos as [number, number, number],
        ...(f.guaranteed ? { guaranteed: true } : {}),
      })),
      // M19a loop backport: the same wind-field law endless uses, derived
      // from the loop's own fan placements — zero generation change.
      loopWindField(track.placements),
    );
    ctx.scene.add(marble.obj);
    trail.setWidth(marble.radius * 0.6);
    boostTrail.setWidth(marble.radius * 1.5);

    if (DEBUG) {
      debugLine = centerline.debugLine();
      ctx.scene.add(debugLine);
    }

    fitShadowsTo(ctx.sun, built.box);
    cams.retarget(centerline.curve, marble);
    // Frame the circuit itself. Scenery can spread far beyond it and
    // used to leave the marble run as a tiny dot in an empty overview.
    const circuitBox = new THREE.Box3().setFromPoints(
      centerline.curve.getPoints(256),
    );
    cams.frame(circuitBox.expandByScalar(1.5));

    if (seedEl) seedEl.textContent = seed;
    const s = track.stats;
    loadingEl.hidden = true;
    loadingEl.classList.remove("failed");
    setStats(
      `${s.theme} • ${s.setting} • ${s.pieceCount} pieces • ` +
        `${track.loop.totalLength.toFixed(0)}u • maxY ${s.maxDeckY}` +
        (built.missing.length ? ` • ${built.missing.length} missing ids` : "") +
        ` • ${
          (performance.now() - t0).toFixed(0)
        }ms • ${genVersion()} • \` inspect`,
    );
  }

  installCua(ctx, () => ({
    mode: "track",
    seed: lastSeed,
    version: genVersion(),
    inspect: inspector.active ? inspector.selection() : undefined,
    camMode: cams.mode,
    marble: marble
      ? {
        s: Number(marble.s.toFixed(2)),
        speed: Number(marble.speed.toFixed(2)),
        pos: marble.obj.position.toArray().map((v: number) =>
          Number(v.toFixed(2))
        ),
      }
      : null,
    stats: lastTrack
      ? {
        theme: lastTrack.stats.theme,
        setting: lastTrack.stats.setting,
        pieces: lastTrack.stats.pieceCount,
        loopLength: Number(lastTrack.loop.totalLength.toFixed(1)),
        maxDeckY: lastTrack.stats.maxDeckY,
        missing: built?.missing.length ?? 0,
      }
      : null,
  }));

  initSeed(regenerate);

  newTrackBtn?.addEventListener("click", () => {
    newSeed();
  });
  camBtn?.addEventListener("click", () => {
    cams.toggle();
  });
  seedEl?.addEventListener("click", () => {
    navigator.clipboard?.writeText(location.href).catch(() => {});
  });
}

// ---------------------------------------------------------------------------

if (!MENU_ACTIVE) {
  loadAssets().then(async (assets) => {
    if (TWIST_DEMO) buildTwistDemo(ctx, assets);
    else if (GALLERY) buildPieceBench(ctx, assets);
    else if (ENDLESS) {
      // Physics is the endless default (?phys=0 falls back to the kinematic
      // rail rider). The wasm module is a separate lazy artifact.
      let physics = null;
      if (params.get("phys") !== "0") {
        try {
          const factory =
            (await import(new URL("/dist/box3d.mjs", location.href).href))
              .default;
          physics = await factory();
        } catch (err) {
          console.warn("[endless] physics module unavailable:", err);
        }
      }
      bootEndless(ctx, assets, decorCatalog(assets), {
        seedEl,
        statsEl,
        newTrackBtn,
        camBtn,
        onError: fail,
        onReady: () => {
          loadingEl.hidden = true;
          loadingEl.classList.remove("failed");
        },
      }, physics);
    } else boot(assets);
    if (!loadingEl.classList.contains("failed")) loadingEl.hidden = true;
  }).catch(fail);
}
